import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, Download, Mail, Plus, Link2, PenLine, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useI18n } from "../i18n/I18nContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Contracts() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("general");
  const [durationYears, setDurationYears] = useState("1");
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [signingId, setSigningId] = useState(null);

  const load = () => {
    api.get("/contracts").then((r) => setItems(r.data)).catch(() => {});
    api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = selected === "general" ? {} : { request_id: selected };
      body.duration_years = parseInt(durationYears, 10);
      await api.post("/contracts/generate", body);
      toast.success(t("contracts.generated"));
      setOpen(false);
      setSelected("general");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const sendEmail = async (id) => {
    setSendingId(id);
    try {
      const { data } = await api.post(`/contracts/${id}/send-email`);
      toast.success(t("contracts.sentTo", { email: data.to }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSendingId(null); }
  };

  const sign = async (id) => {
    setSigningId(id);
    try {
      const { data } = await api.post(`/contracts/${id}/sign`);
      setItems((prev) => prev.map((c) => (c.contract_id === id ? data : c)));
      toast.success(t("contracts.signedToast"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSigningId(null); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl" data-testid="contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-gold">{t("contracts.overline")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("contracts.title")}</h1>
          <p className="text-muted-foreground mt-2">{t("contracts.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="generate-contract-btn" className="rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300">
              <Plus className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("contracts.generate")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-white border-sand max-w-lg" data-testid="generate-contract-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-midnight">{t("contracts.dialogTitle")}</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">{t("contracts.dialogText")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-midnight text-sm">{t("contracts.objectiveLabel")}</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger className="mt-1.5 rounded-none border-sand" data-testid="contract-request-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    <SelectItem value="general">{t("contracts.generalOption")}</SelectItem>
                    {requests.map((r) => (
                      <SelectItem key={r.request_id} value={r.request_id}>
                        {r.type === "specific_job" ? t("contracts.optJob") : t("contracts.optPartnership")} · {r.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-midnight text-sm">{t("common.durationYears")}</Label>
                <Select value={durationYears} onValueChange={setDurationYears}>
                  <SelectTrigger className="mt-1.5 rounded-none border-sand" data-testid="contract-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    {[1, 2, 3, 4, 5].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={generating} data-testid="confirm-generate-contract-btn"
                className="w-full h-11 rounded-none bg-midnight text-bone hover:bg-midnight/90 transition-all duration-300">
                {generating ? t("contracts.generating") : t("contracts.confirm")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-sand bg-white p-14 text-center" data-testid="contracts-empty">
          <FileSignature className="h-8 w-8 text-muted-foreground mx-auto" strokeWidth={1.5} />
          <p className="text-muted-foreground mt-4">{t("contracts.empty")}</p>
        </div>
      ) : (
        <div className="grid gap-5" data-testid="contracts-list">
          {items.map((c) => (
            <div key={c.contract_id} className="border border-sand bg-white p-6 hover:shadow-[0_12px_40px_rgba(11,19,43,0.06)] transition-all duration-300"
              data-testid={`contract-row-${c.contract_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-midnight">{c.contract_number}</span>
                    {c.signed ? (
                      <Badge variant="outline" className="rounded-none border-green-500/40 bg-green-50 text-green-700 text-xs flex items-center gap-1" data-testid={`contract-signed-badge-${c.contract_id}`}>
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> {t("contracts.signedBadge")}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-none border-gold/40 bg-gold/10 text-midnight text-xs capitalize">{c.status}</Badge>
                    )}
                  </div>
                  {c.signed && (
                    <p className="text-xs text-green-700 mt-1" data-testid={`contract-signed-date-${c.contract_id}`}>
                      {t("contracts.signedOn", { date: c.signed_at_display })} · {c.signed_by}
                    </p>
                  )}
                  {c.request_title && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-midnight" data-testid={`contract-linked-${c.contract_id}`}>
                      <Link2 className="h-4 w-4 text-gold" strokeWidth={1.5} />
                      <span>{t("contracts.linkedRequest")} <span className="font-medium">{c.request_title}</span></span>
                    </div>
                  )}
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-10 gap-y-1 text-sm text-muted-foreground">
                    <span>{t("contracts.company")}: <span className="text-midnight">{c.company_name}</span></span>
                    <span>{t("contracts.successFee")}: <span className="text-midnight font-medium">{c.success_fee_pct}%</span></span>
                    <span>{t("contracts.start")}: <span className="text-midnight">{c.start_date}</span></span>
                    <span>{t("contracts.end")}: <span className="text-midnight">{c.end_date}</span></span>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                {!c.signed && (
                  <Button onClick={() => sign(c.contract_id)} disabled={signingId === c.contract_id}
                    data-testid={`contract-sign-${c.contract_id}`}
                    className="rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300">
                    <PenLine className="h-4 w-4 me-2" strokeWidth={1.5} /> {signingId === c.contract_id ? t("contracts.signing") : t("contracts.sign")}
                  </Button>
                )}
                <a href={`${BACKEND}/api/contracts/${c.contract_id}/pdf`} target="_blank" rel="noopener noreferrer"
                  data-testid={`contract-pdf-${c.contract_id}`}>
                  <Button variant="outline" className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
                    <Download className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("contracts.downloadPdf")}
                  </Button>
                </a>
                <Button onClick={() => sendEmail(c.contract_id)} disabled={sendingId === c.contract_id} variant="outline"
                  data-testid={`contract-email-${c.contract_id}`}
                  className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
                  <Mail className="h-4 w-4 me-2" strokeWidth={1.5} /> {sendingId === c.contract_id ? t("contracts.sending") : t("contracts.sendEmail")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
